import { useEffect, useRef, useState } from "react";
import { api, eur, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Plus, Upload, Loader2, ImageIcon, Trash2, Search, Copy, Eye, EyeOff, ArrowUp, ArrowDown, Heart } from "lucide-react";
import TopProductsPanel from "@/components/dashboard/TopProductsPanel";
import { RecipeDialog } from "@/components/dashboard/RecipeEditor";
import { DndContext, pointerWithin, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChefHat } from "lucide-react";

// Carte triable : seule la poignée déclenche le glisser (le reste de la carte garde le défilement tactile).
function SortableCard({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition, touchAction: "pan-y" };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${isDragging ? "z-20 opacity-90 scale-[1.02]" : ""}`}>
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`menu-drag-${id}`} aria-label="Déplacer le plat"
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-8 h-12 rounded-lg grid place-items-center text-stone-500 hover:text-[#C69C4A] hover:bg-[#0C1713]/70 cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}>
        <GripVertical className="w-5 h-5" />
      </button>
      {children}
    </div>
  );
}

const STATION_FR = { kitchen: "Cuisine", service: "Service", bar: "Bar" };
const CATEGORIES = ["Formules", "Entrées", "Salades", "Plats", "Burgers", "Pâtes", "Accompagnements", "Sauces", "Fromages", "Desserts", "Boissons", "Cocktails", "Menu Enfant"];

export default function MenuManager({ slug, products, categories, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [vatRates, setVatRates] = useState([]);
  useEffect(() => { api.vatRates(slug).then((r) => setVatRates(r.rates || [])).catch(() => setVatRates([])); }, [slug]);
  const [catFilter, setCatFilter] = useState("Toutes");
  const [recipeFor, setRecipeFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, okMsg) => {
    if (busy) return;
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await onRefresh(); } catch (e) { toast.error("Erreur"); } finally { setBusy(null); }
  };

  const toggleAvailable = (p) => run(p.id, () => api.updateProduct(slug, p.id, { available: !p.available }),
    !p.available ? `${p.name} : à nouveau disponible` : `${p.name} : indisponible (visible, non commandable)`);

  const toggleHidden = (p) => run(p.id, () => api.updateProduct(slug, p.id, { hidden: !p.hidden }),
    !p.hidden ? `${p.name} masqué du menu client` : `${p.name} affiché dans le menu client`);

  // Réordonnancement sur la liste COMPLÈTE de la catégorie (jamais sur la liste filtrée par la recherche).
  const fullCat = (cat) => products.filter((p) => p.category === cat);
  const reorderFull = (cat, aId, bId, insert) => {
    const ids = fullCat(cat).map((p) => p.id);
    const from = ids.indexOf(aId), to = ids.indexOf(bId);
    if (from < 0 || to < 0) return null;
    if (insert) return arrayMove(ids, from, to);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    return ids;
  };
  const move = (items, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const ids = reorderFull(items[idx].category, items[idx].id, items[j].id, false);
    if (ids) return run(items[idx].id, () => api.reorderProducts(slug, ids), "Ordre enregistré");
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (cat) => ({ active, over }) => {
    if (!over || active.id === over.id) return; // hors de la catégorie (pointerWithin) → aucun changement
    const ids = reorderFull(cat, active.id, over.id, true);
    if (ids) run(active.id, () => api.reorderProducts(slug, ids), "Ordre enregistré");
  };

  const duplicate = async (p) => {
    try {
      const { id, created_at, updated_at, sort_order, ...rest } = p;
      await api.createProduct(slug, { ...rest, name: `${p.name} (copie)` });
      toast.success("Produit dupliqué");
      onRefresh();
    } catch (e) { toast.error("Erreur lors de la duplication"); }
  };

  const cats = categories.length ? categories.map((c) => c.name) : CATEGORIES;
  const q = query.trim().toLowerCase();
  const match = (p) => (catFilter === "Toutes" || p.category === catFilter) && (!q || p.name.toLowerCase().includes(q));
  const grouped = cats.map((c) => ({ name: c, items: products.filter((p) => p.category === c && match(p)) }));
  const shownCount = products.filter(match).length;

  return (
    <div>
      <TopProductsPanel slug={slug} />
      <div className="flex justify-between items-center mb-4">
        <p className="text-stone-400 text-sm">{shownCount} / {products.length} produits</p>
        <Button data-testid="menu-add-product" onClick={() => setCreating(true)} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter un produit
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="menu-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un produit…" className="pl-9 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
        </div>
        <select data-testid="menu-cat-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="bg-[#111F1A] border border-[#1B3028] rounded-xl px-3 py-2 text-sm text-stone-100 sm:w-56">
          <option value="Toutes">Toutes les catégories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="space-y-6">
        {grouped.map((g) => g.items.length > 0 && (
          <div key={g.name}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300">{g.name}</h3>
              {(() => { const c = categories.find((x) => x.name === g.name); return c ? (
                <label className="text-xs text-stone-300 flex items-center gap-1.5">Destination par défaut
                  <select data-testid={`cat-station-${c.id}`} value={c.default_station || "kitchen"} onChange={async (e) => { try { await api.updateCategory(slug, c.id, { default_station: e.target.value }); toast.success(`${g.name} → ${STATION_FR[e.target.value]}`); onRefresh(); } catch { toast.error("Erreur"); } }}
                    className="bg-[#111F1A] border border-[#1B3028] rounded-lg px-2 py-1 text-xs text-stone-100">
                    <option value="kitchen">Cuisine</option><option value="service">Service</option><option value="bar">Bar</option>
                  </select>
                </label>
              ) : null; })()}
            </div>
            <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onDragEnd(g.name)}>
            <SortableContext items={g.items.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
              {g.items.map((p, idx) => (
                <SortableCard key={p.id} id={p.id} disabled={!!busy || g.items.length < 2}>
                <div data-testid={`menu-item-${p.id}`} data-busy={busy === p.id ? "true" : "false"} className={`flex gap-3 rounded-2xl bg-[#111F1A] border p-3 pl-9 transition-opacity ${busy === p.id ? "opacity-60 pointer-events-none" : ""} ${p.hidden ? "border-dashed border-stone-600 opacity-75" : "border-[#1B3028]"}`}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className={`w-16 h-16 rounded-xl object-cover shrink-0 ${!p.available || p.hidden ? "grayscale opacity-50" : ""}`} />
                  ) : (
                    <div className="w-16 h-16 rounded-xl shrink-0 grid place-items-center bg-[#16241E] border border-[#1B3028] text-[#C69C4A]" title="Aucune photo"><ImageIcon className="w-5 h-5" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-100 truncate flex items-center gap-1.5">{p.name} {p.is_popular && <Heart className="w-3 h-3 text-[#C69C4A] fill-[#C69C4A]" title="Coup de cœur" />} {p.hidden && <span data-testid={`menu-hidden-badge-${p.id}`} className="text-[9px] font-bold uppercase rounded px-1.5 py-0.5 bg-stone-700 text-stone-200">Masqué</span>} {p.station && <span data-testid={`menu-station-badge-${p.id}`} className="text-[9px] font-bold uppercase rounded px-1.5 py-0.5 bg-[#1B3028] text-stone-300">{STATION_FR[p.station]}{p.station_auto ? " (auto)" : ""}</span>} {p.station_review && <span data-testid={`menu-station-review-${p.id}`} title="Destination à confirmer : modifiez le poste dans la fiche produit" className="text-[9px] font-bold uppercase rounded px-1.5 py-0.5 bg-amber-500/20 text-amber-300">Poste à vérifier</span>}</p>
                    <p className="text-sm font-bold text-[#C69C4A]">{eur(p.price)} <span data-testid={`menu-vat-${p.id}`} className={`ml-1 text-[10px] font-semibold ${p.vat_rate == null ? "text-amber-400" : "text-stone-500"}`}>{p.vat_rate == null ? "TVA non renseignée" : `TVA ${String(p.vat_rate).replace(".", ",")} %`}</span></p>
                    <div className="flex items-center justify-between mt-2 gap-1 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Switch data-testid={`menu-toggle-${p.id}`} checked={p.available} onCheckedChange={() => toggleAvailable(p)} className="data-[state=checked]:bg-emerald-600" />
                        <span className={`text-[11px] font-medium ${p.available ? "text-emerald-400" : "text-orange-300"}`}>{p.available ? "Disponible" : "Indisponible"}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button data-testid={`menu-up-${p.id}`} onClick={() => move(g.items, idx, -1)} disabled={idx === 0} className="text-stone-400 hover:text-[#C69C4A] p-1 disabled:opacity-25" title="Monter"><ArrowUp className="w-4 h-4" /></button>
                        <button data-testid={`menu-down-${p.id}`} onClick={() => move(g.items, idx, 1)} disabled={idx === g.items.length - 1} className="text-stone-400 hover:text-[#C69C4A] p-1 disabled:opacity-25" title="Descendre"><ArrowDown className="w-4 h-4" /></button>
                        <button data-testid={`menu-hide-${p.id}`} onClick={() => toggleHidden(p)} className={`p-1 ${p.hidden ? "text-orange-300 hover:text-emerald-400" : "text-stone-400 hover:text-orange-300"}`} title={p.hidden ? "Afficher dans le menu client" : "Masquer du menu client"}>
                          {p.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button data-testid={`menu-recipe-${p.id}`} onClick={() => setRecipeFor(p)} className="text-stone-400 hover:text-[#C69C4A] p-1" title="Fiche recette (stocks)">
                          <ChefHat className="w-4 h-4" />
                        </button>
                        <button data-testid={`menu-duplicate-${p.id}`} onClick={() => duplicate(p)} className="text-stone-400 hover:text-[#C69C4A] p-1" title="Dupliquer">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button data-testid={`menu-edit-${p.id}`} onClick={() => setEditing(p)} className="text-stone-400 hover:text-[#C69C4A] p-1" title="Modifier">
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                </SortableCard>
              ))}
            </div>
            </SortableContext>
            </DndContext>
          </div>
        ))}
      </div>

      {recipeFor && <RecipeDialog slug={slug} product={recipeFor} onClose={() => setRecipeFor(null)} />}
      {editing && (
        <ProductForm slug={slug} product={editing} vatRates={vatRates} onClose={() => setEditing(null)} onDone={() => { setEditing(null); onRefresh(); }} />
      )}
      {creating && (
        <ProductForm slug={slug} vatRates={vatRates} onClose={() => setCreating(false)} onDone={() => { setCreating(false); onRefresh(); }} />
      )}
    </div>
  );
}

function ProductForm({ slug, product, vatRates = [], onClose, onDone }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price ?? "",
    category: product?.category || "Plats",
    image: product?.image || "",
    available: product?.available ?? true,
    product_type: product?.product_type || "simple",
    option_groups: product?.option_groups || [],
    formula_courses: product?.formula_courses || [],
    vat_rate: product?.vat_rate ?? null,
    hidden: product?.hidden ?? false,
    is_popular: product?.is_popular ?? false,
    unavailable_label: product?.unavailable_label || "neutral",
    station: product?.station || "inherit",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Sélectionnez une image"); return; }
    setUploading(true);
    try {
      const res = await api.uploadPhoto(slug, file);
      set("image", fileUrl(res.url));
      toast.success("Photo importée");
    } catch (err) { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || form.price === "") { toast.error("Nom et prix requis"); return; }
    setSaving(true);
    try {
      const body = { ...form, price: parseFloat(form.price) };
      if (body.vat_rate == null) { delete body.vat_rate; if (isEdit && product?.vat_rate != null) body.clear_vat_rate = true; }
      if (body.station === "inherit") { delete body.station; if (isEdit && product?.station) body.clear_station = true; }
      if (isEdit) await api.updateProduct(slug, product.id, body);
      else await api.createProduct(slug, { ...body, image: form.image || "" });
      toast.success(isEdit ? "Produit modifié" : "Produit créé");
      onDone();
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form-dialog" className="max-w-md rounded-3xl bg-[#FBF9F5] max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#123026]">{isEdit ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
        <DialogDescription className="sr-only">Formulaire de {isEdit ? "modification" : "création"} de produit</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Nom"><Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white rounded-xl" /></Field>
          <Field label="Description"><Textarea data-testid="pf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-white rounded-xl resize-none" rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prix (€)"><Input data-testid="pf-price" type="number" step="0.10" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-white rounded-xl" /></Field>
            <Field label="Catégorie">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="pf-category" className="bg-white rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Poste de préparation / destination">
            <Select value={form.station} onValueChange={(v) => set("station", v)}>
              <SelectTrigger data-testid="pf-station" className="bg-white rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Par défaut de la catégorie</SelectItem>
                <SelectItem value="kitchen">Cuisine (KDS)</SelectItem>
                <SelectItem value="service">Service / Serveur (sans cuisine)</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Photo du plat">
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-xl bg-white border border-[#EAE5DC] overflow-hidden shrink-0 grid place-items-center">
                {form.image ? (
                  <img src={fileUrl(form.image)} alt="aperçu" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#C9CFC9]" />
                )}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="pf-file-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full w-full">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {uploading ? "Import..." : "Importer une photo"}
                </Button>
                <Input data-testid="pf-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="ou coller une URL" className="bg-white rounded-xl mt-2 text-xs" />
                {form.image ? (
                  <button type="button" data-testid="pf-image-delete" onClick={() => set("image", "")} className="mt-2 inline-flex items-center gap-1.5 text-xs text-red-500 font-semibold">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer la photo
                  </button>
                ) : (
                  <p className="mt-2 text-[11px] text-[#8A918D]">Aucune photo — un placeholder élégant sera affiché.</p>
                )}
              </div>
            </div>
          </Field>
          <Field label="Taux de TVA (à valider par le restaurant / comptable)">
            <Select value={form.vat_rate == null ? "none" : String(form.vat_rate)} onValueChange={(v) => set("vat_rate", v === "none" ? null : parseFloat(v))}>
              <SelectTrigger data-testid="pf-vat" className={`bg-white rounded-xl ${form.vat_rate == null ? "border-amber-400 text-amber-700" : ""}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">TVA non renseignée</SelectItem>
                {vatRates.map((r) => <SelectItem key={r.rate} value={String(r.rate)}>{String(r.rate).replace(".", ",")} % — {r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <OptionsEditor form={form} set={set} />
          <div className="rounded-2xl bg-white border border-[#EAE5DC] p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-semibold text-[#123026]">Disponible</p><p className="text-[11px] text-[#8A918D]">Désactivé : le plat reste visible mais ne peut pas être commandé.</p></div>
              <Switch data-testid="pf-available" checked={form.available} onCheckedChange={(v) => set("available", v)} className="data-[state=checked]:bg-emerald-600" />
            </div>
            {!form.available && (
              <Field label="Mention affichée au client (sous-titre « Indisponible pour le moment » toujours affiché)">
                <Select value={form.unavailable_label} onValueChange={(v) => set("unavailable_label", v)}>
                  <SelectTrigger data-testid="pf-unavailable-label" className="bg-white rounded-xl w-full min-w-0 [&>span]:truncate [&>span]:text-left"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-3rem)]">
                    <SelectItem value="success">« Victime de son succès ! »</SelectItem>
                    <SelectItem value="neutral">« Indisponible pour le moment » (neutre)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-semibold text-[#123026]">Masquer du menu client</p><p className="text-[11px] text-[#8A918D]">Le plat disparaît côté client mais reste enregistré ici.</p></div>
              <Switch data-testid="pf-hidden" checked={form.hidden} onCheckedChange={(v) => set("hidden", v)} className="data-[state=checked]:bg-orange-500" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-semibold text-[#123026]">❤️ Coup de cœur</p><p className="text-[11px] text-[#8A918D]">Mise en avant manuelle, indépendante des « plus commandés ».</p></div>
              <Switch data-testid="pf-popular" checked={form.is_popular} onCheckedChange={(v) => set("is_popular", v)} className="data-[state=checked]:bg-[#C69C4A]" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">Annuler</Button>
          <Button data-testid="pf-save" onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#123026] hover:bg-[#0A1C16] text-white">{saving ? "..." : "Enregistrer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function OptionsEditor({ form, set }) {
  const type = form.product_type || "simple";
  const groups = form.option_groups || [];
  const courses = form.formula_courses || [];
  const uid = () => Math.random().toString(36).slice(2, 8);

  const setGroups = (g) => set("option_groups", g);
  const setCourses = (c) => set("formula_courses", c);

  const addGroup = () => setGroups([...groups, { id: uid(), name: "Nouveau groupe", type: "single", required: true, values: [] }]);
  const updGroup = (i, k, v) => setGroups(groups.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const delGroup = (i) => setGroups(groups.filter((_, idx) => idx !== i));
  const addVal = (i) => updGroup(i, "values", [...(groups[i].values || []), { name: "Option", price: 0 }]);
  const updVal = (i, j, k, v) => updGroup(i, "values", groups[i].values.map((x, idx) => idx === j ? { ...x, [k]: k === "price" ? parseFloat(v || 0) : v } : x));
  const delVal = (i, j) => updGroup(i, "values", groups[i].values.filter((_, idx) => idx !== j));

  const addCourse = () => setCourses([...courses, { id: uid(), name: "Nouveau plat", required: true, options: [] }]);
  const updCourse = (i, k, v) => setCourses(courses.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const delCourse = (i) => setCourses(courses.filter((_, idx) => idx !== i));
  const addCOpt = (i) => updCourse(i, "options", [...(courses[i].options || []), { name: "Choix", price: 0 }]);
  const updCOpt = (i, j, k, v) => updCourse(i, "options", courses[i].options.map((x, idx) => idx === j ? { ...x, [k]: k === "price" ? parseFloat(v || 0) : v } : x));
  const delCOpt = (i, j) => updCourse(i, "options", courses[i].options.filter((_, idx) => idx !== j));

  return (
    <div className="rounded-2xl bg-[#F2EEE4] border border-[#EAE5DC] p-3 space-y-3" data-testid="options-editor">
      <Field label="Type de produit">
        <Select value={type} onValueChange={(v) => set("product_type", v)}>
          <SelectTrigger data-testid="pf-type" className="bg-white rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">Simple (ajout direct)</SelectItem>
            <SelectItem value="configurable">Configurable (options)</SelectItem>
            <SelectItem value="formula">Formule (menu à composer)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {type === "configurable" && (
        <div className="space-y-2">
          {groups.map((g, i) => (
            <div key={g.id} className="rounded-xl bg-white border border-[#EAE5DC] p-2.5 space-y-2" data-testid={`opt-group-${i}`}>
              <div className="flex gap-2 items-center">
                <Input value={g.name} onChange={(e) => updGroup(i, "name", e.target.value)} className="rounded-lg h-8 text-sm flex-1" />
                <Select value={g.type} onValueChange={(v) => updGroup(i, "type", v)}>
                  <SelectTrigger className="w-24 h-8 text-xs bg-white rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Unique</SelectItem><SelectItem value="multi">Multiple</SelectItem></SelectContent>
                </Select>
                <label className="flex items-center gap-1 text-[11px] text-[#5C615E]"><Switch checked={g.required} onCheckedChange={(v) => updGroup(i, "required", v)} className="data-[state=checked]:bg-emerald-600 scale-75" />Oblig.</label>
                <button onClick={() => delGroup(i)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              {(g.values || []).map((v, j) => (
                <div key={j} className="flex gap-2 items-center pl-2">
                  <Input value={v.name} onChange={(e) => updVal(i, j, "name", e.target.value)} className="rounded-lg h-7 text-xs flex-1" />
                  <Input type="number" step="0.5" value={v.price} onChange={(e) => updVal(i, j, "price", e.target.value)} className="rounded-lg h-7 text-xs w-20" placeholder="+€" />
                  <button onClick={() => delVal(i, j)} className="text-stone-400 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => addVal(i)} className="text-xs text-[#C69C4A] font-semibold pl-2">+ Valeur</button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addGroup} data-testid="add-option-group" className="rounded-full w-full text-sm h-9"><Plus className="w-4 h-4 mr-1" /> Ajouter un groupe d'options</Button>
        </div>
      )}

      {type === "formula" && (
        <div className="space-y-2">
          {courses.map((c, i) => (
            <div key={c.id} className="rounded-xl bg-white border border-[#EAE5DC] p-2.5 space-y-2" data-testid={`formula-course-${i}`}>
              <div className="flex gap-2 items-center">
                <Input value={c.name} onChange={(e) => updCourse(i, "name", e.target.value)} className="rounded-lg h-8 text-sm flex-1" placeholder="Ex: Entrée" />
                <button onClick={() => delCourse(i)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              {(c.options || []).map((o, j) => (
                <div key={j} className="flex gap-2 items-center pl-2">
                  <Input value={o.name} onChange={(e) => updCOpt(i, j, "name", e.target.value)} className="rounded-lg h-7 text-xs flex-1" />
                  <Input type="number" step="0.5" value={o.price} onChange={(e) => updCOpt(i, j, "price", e.target.value)} className="rounded-lg h-7 text-xs w-20" placeholder="+€" />
                  <button onClick={() => delCOpt(i, j)} className="text-stone-400 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => addCOpt(i)} className="text-xs text-[#C69C4A] font-semibold pl-2">+ Choix</button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addCourse} data-testid="add-formula-course" className="rounded-full w-full text-sm h-9"><Plus className="w-4 h-4 mr-1" /> Ajouter un plat (entrée/plat/dessert)</Button>
        </div>
      )}
    </div>
  );
}
